<template>
  <div class="street-input">
    <el-autocomplete
      ref="street"
      v-model="streetText"
      :fetch-suggestions="fetchStreets"
      :placeholder="placeholder"
      :tabindex="tabindex"
      :trigger-on-focus="false"
      :class="{ deleted: deleted }"
      popper-class="street-input-popper"
      class="input"
      clearable
      @clear="handleClear"
      @select="handleSelect"
    >
      <div slot-scope="{ item }" style="display: flex; align-items: center; gap: 10px">
        <span v-if="item.poi" class="poi-icon">POI</span>
        <i v-if="item.id === -1" class="mr-2 fas fa-fw fa-search"></i>
        <span style="margin-right: auto" class="ellipsis">
          {{ item.value }}
        </span>
        <span
          v-if="showStop && item.stopBelong && item.stopBelong.length > 0"
          class="stop"
          style="font-style: italic"
        >
          <template v-if="item.stopBelong.length === 1">
            Parada {{ item.stopBelong[0].replace(/^Parada /i, "") }}
          </template>
          <template v-else>
            <el-tooltip :content="item.stopBelong.join(', ')">
              <span>Varias paradas</span>
            </el-tooltip>
          </template>
        </span>
        <span v-if="isMulticentral" class="central">
          {{ resolveCentralName(item.centralId) }}
        </span>
      </div>
    </el-autocomplete>
    <OnlineStreetSearchDrawer
      :wgId="workgroupId"
      :centralId="centralId"
      :initial-search="initialOnlineStreetSearchText"
      :show="onlineStreetSearchDrawerFlag"
      @created="handleCreated"
      @changeShow="onlineStreetSearchDrawerFlag = $event"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getStreetById } from "@/api/operations/streets";
import cache from "@/mixins/cache";
import OnlineStreetSearchDrawer from "./OnlineStreetSearchDrawer.vue";

export default {
  name: "StreetInput",
  components: { OnlineStreetSearchDrawer },
  props: {
    value: [Number, String],
    showStop: Boolean,
    workgroupId: Number,
    centralId: Number,
    tabindex: [Number, String],
    ignorePois: Boolean,
    limitToCentral: Boolean,
    placeholder: {
      type: String,
      default: "Introduce una calle o punto de interés",
    },
    disablePersistedCache: Boolean,
  },
  computed: {
    ...mapGetters("app", ["centrals"]),
    isMulticentral() {
      return this.centrals.length > 1;
    },
  },
  mixins: [cache],
  data() {
    return {
      streetText: "",
      initialOnlineStreetSearchText: "",
      onlineStreetSearchDrawerFlag: false,
      persistedCache: true,
      deleted: false,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.fillStreetText();
      },
    },
  },
  created() {
    if (this.disablePersistedCache) {
      this.persistedCache = false;
    }
    if (!this.cached) {
      this.fillStreetText();
    }
  },
  methods: {
    ...mapActions("clientStreets", ["getStreets"]),
    resolveCentralName(centralId) {
      return this.centrals.find((c) => c.id === centralId)?.name;
    },
    handleClear() {
      this.streetText = "";
      this.$emit("input", null);
      this.$emit("setStreet", null, true);
      this.$emit("clearStreet");
      // This avoid a bug that prevents the user from
      // search for new streets after clearing the autocomplete
      // while the inner input is focused.
      this.$refs.street && this.$refs.street.$el.querySelector("input").blur();
      this.$nextTick(() => {
        this.$refs.street && this.$refs.street.$el.querySelector("input").focus();
      });
      this.$forceUpdate();
    },
    async handleCreated(id, number) {
      this.onlineStreetSearchDrawerFlag = false;
      this.handleSelect({ id });
      if (number) {
        this.$emit("setPortal", number);
      }
    },
    async handleSelect(street) {
      if (street?.id === -1) {
        this.initialOnlineStreetSearchText = street.text;
        this.streetText = "";
        this.onlineStreetSearchDrawerFlag = true;
      } else if (street?.id) {
        try {
          const streetData = await getStreetById(street.id, this.workgroupId);
          this.$emit("input", streetData.id);
          this.propagateStreet(streetData, true);
        } catch (e) {
          console.error(e.message);
        }
      } else {
        this.handleClear();
      }
    },
    async fetchStreets(text, callback) {
      const params = {
        wgId: this.workgroupId,
        text: text,
      };
      if (this.centralId && this.limitToCentral) {
        params.centralId = this.centralId;
      }
      let streets = await this.getStreets(params);
      if (this.ignorePois) {
        streets = streets.filter((s) => !s.poi);
      }
      callback([
        ...streets,
        {
          id: -1,
          value: "Buscar online",
          text,
        },
      ]);
    },
    async fillStreetText() {
      if (this.value) {
        this.deleted = false;
        const streetData = await getStreetById(this.value, this.workgroupId);
        this.streetText = streetData.name;
        if (streetData.hint) {
          this.streetText += ` (${streetData.hint})`;
        }
        if (streetData.deleted) {
          this.deleted = true;
          this.streetText += " - eliminada";
        }
        this.propagateStreet(streetData);
      }
    },
    propagateStreet(street, manual = false) {
      this.$emit("setStreet", street, manual);
    },
    focus() {
      this.$refs.street.focus();
    },
    async appendToStreetText(text) {
      await this.fillStreetText();
      this.streetText = `${this.streetText}${text}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.street-input {
  position: relative;
  flex: 1;
}

::v-deep .deleted .el-input__inner {
  text-decoration: line-through !important;
  color: red !important;
}

.street-input ::v-deep .el-autocomplete {
  width: 100%;
}

.poi-icon {
  text-transform: uppercase;
  background-color: #e98c00;
  font-size: 10px;
  color: white;
  padding: 0 5px;
  height: 16px;
  line-height: 16px;
  margin-right: 4px;
  border-radius: 10px;
  letter-spacing: -0.5px;
  font-weight: bold;
}

::v-deep .change {
  width: 100%;
}
</style>

<style lang="scss" scoped>
.central {
  font-size: 12px;
  color: #999;
}
</style>

<style>
.street-input-popper {
  width: auto !important;
  min-width: 800px !important;
  max-width: 1400px !important;
}
</style>
